import axios, { AxiosHeaders } from "axios";
import { getToken } from "../utils/storage.ts";

const API_BASE = import.meta.env.VITE_SUPABASE_FUNCTION_URL;
const API_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY; // Supabase anon key

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
    // Both headers are recommended by Supabase when calling Edge Functions
    // Ensure apikey is always present for Edge Functions authorization context
    // ...(API_KEY ? { apikey: API_KEY } : {}),
  },
});

/**
 * Get authentication token - checks both paid user token and guest token
 * Returns the first available token in order: paid token > guest token
 */
function getAuthToken(): string | null {
  // First, check for paid user token
  const paidToken = getToken();
  if (paidToken) {
    return paidToken;
  }

  // If no paid token, check for guest token
  try {
    const guestExamUser = localStorage.getItem("guestExamUser");
    if (guestExamUser) {
      const parsed = JSON.parse(guestExamUser);
      if (parsed?.token) {
        return parsed.token;
      }
    }
  } catch (e) {
    console.warn("Error parsing guestExamUser:", e);
  }

  // Fallback to standalone guest token
  const guestToken = localStorage.getItem("guestToken");
  if (guestToken) {
    return guestToken;
  }

  return null;
}

apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    const headers = config.headers
      ? AxiosHeaders.from(config.headers)
      : new AxiosHeaders();

    // Respect an explicitly provided Authorization header (e.g., per-request tokens)
    // Only add token if Authorization header is not already set
    if (!headers.has("Authorization") && token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    config.headers = headers;
    return config;
  },
  (error) => Promise.reject(error)
);

export default apiClient;
